import React from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  ChoiceField,
  makeReadField,
  missingOption,
  refFieldsOf,
  useDependsOn,
  useDroppedRowsWarning,
  validKeysOf,
  wrongShape,
} from '@ns/plugin-picker-shared';

import { MatchAuthorError } from './match';
import { queryChoiceState } from './query-state';
import { subscriptionIdFrom } from './refs';
import {
  buildResourceGroupQuery,
  type ResourceGroupPickerUiOptions,
  resourceGroupQueryEnabled,
  selectResourceGroups,
} from './resource-group-query';
import { AZURE_PICKER_API_VERSION } from './subscription-query';
import type { AzureResourceGroup } from './types';
import { useQueryResources } from './use-query-resources';

/**
 * Picks a resource group within a subscription chosen elsewhere in the form.
 *
 * @remarks
 * `subscriptionRef` names the field holding the subscription. Without one there
 * is no list, because the subscription is the scope of the call.
 */
export function AzureResourceGroupPickerComponent(
  props: FieldExtensionComponentProps<
    AzureResourceGroup,
    ResourceGroupPickerUiOptions
  >,
): React.ReactElement {
  const uiOptions = props.uiSchema['ui:options'];
  const subscriptionRef = uiOptions?.subscriptionRef;

  // The fields the filter and exclude sides reference, in two sets. `waitFor`
  // joins `subscriptionRef` as a dependency, because a reference that has not
  // arrived yet must not produce a list; an optional reference opts
  // out of waiting and is absent from it. `all` is the memo key, so the query
  // is rebuilt when an optional value arrives too.
  const { all, waitFor } = React.useMemo(
    () => refFieldsOf(uiOptions?.filter, uiOptions?.exclude),
    [uiOptions?.filter, uiOptions?.exclude],
  );
  const dependsOn = React.useMemo(
    () =>
      typeof subscriptionRef === 'string'
        ? [subscriptionRef, ...waitFor]
        : waitFor,
    [subscriptionRef, waitFor],
  );
  const state = useDependsOn(props, dependsOn);

  const subscriptionId = subscriptionIdFrom(state, subscriptionRef);

  // `formContext.formData` is the whole form; `props.formData` would be this
  // field's own value. The picker has `rootSchema`, so a name outside the form
  // is a typo rather than an untouched field (`author-error`).
  const validKeys = validKeysOf(props);
  const allFormData = props.formContext?.formData ?? {};
  const refKey = JSON.stringify(
    [...all].sort().map(field => [field, allFormData[field]]), // NOSONAR: sorts strings (field names); code-unit order is intended
  );

  // An author error in `filter` or `exclude` must be reported, not thrown:
  // throwing here tears down the whole form, so the filler gets a blank page
  // and nothing says which template is wrong.
  // It reaches the field as a fatal `failed` state, and the picker turns itself
  // off so no call goes out on a query it could not build.
  const { authorError, query } = React.useMemo(() => {
    // Without a subscription there is no list at all, because the subscription
    // is the scope of the call. A reference is one field name, so a list or an
    // object there is a mistake too. Whether that field
    // EXISTS is `resolveDependsOn`'s answer to give.
    if (subscriptionRef === undefined) {
      return {
        authorError: missingOption(
          'subscriptionRef',
          'it names the field holding the subscription',
        ),
        query: '',
      };
    }
    if (typeof subscriptionRef !== 'string') {
      return {
        authorError: wrongShape(
          'subscriptionRef',
          'one field name, written as a plain word',
        ),
        query: '',
      };
    }

    try {
      const readField = makeReadField(allFormData, validKeys, 'author-error');
      return {
        authorError: undefined,
        query: buildResourceGroupQuery(readField, {
          exclude: uiOptions?.exclude,
          filter: uiOptions?.filter,
        }),
      };
    } catch (error) {
      // Only an author mistake becomes a message for the author. Anything else
      // is our bug, and it travels on to the error boundary rather than
      // reaching the template writer as advice he cannot act on.
      if (error instanceof MatchAuthorError) {
        return { authorError: error.message, query: '' };
      }
      throw error;
    }
    // `refKey` carries the referenced values, so the query follows them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionRef, uiOptions?.exclude, uiOptions?.filter, refKey]);

  const enabled =
    authorError === undefined &&
    state.kind !== 'awaiting' &&
    resourceGroupQueryEnabled(subscriptionId);

  const resourceGroups = useQueryResources(
    {
      apiVersion: AZURE_PICKER_API_VERSION,
      query,
      // The subscription travels as a typed argument, never as query text.
      subscriptions: subscriptionId === undefined ? [] : [subscriptionId],
    },
    { enabled, select: selectResourceGroups },
  );
  const answer = resourceGroups.data;
  useDroppedRowsWarning(
    'Azure resource groups',
    answer?.droppedRowCount,
    resourceGroups.dataUpdatedAt,
  );

  const selected = props.formData;
  const selectedKey =
    typeof selected?.id === 'string' ? selected.id.toLowerCase() : null;

  return (
    <ChoiceField<AzureResourceGroup>
      description={props.schema.description}
      emptyText="No resource groups"
      getKey={item => item.id.toLowerCase()}
      getLabel={item => item.name}
      items={answer?.items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      required={props.required}
      selectedKey={selectedKey}
      selectedLabel={typeof selected?.name === 'string' ? selected.name : null}
      sourceKey={query}
      state={queryChoiceState({
        authorError,
        dependsOn: state,
        droppedRowCount: answer?.droppedRowCount,
        error: resourceGroups.error,
        hasItems: (answer?.items.length ?? 0) > 0,
        isError: resourceGroups.isError,
        isPending: enabled && resourceGroups.isPending,
        page: answer?.page,
        required: props.required,
        retry: () => void resourceGroups.refetch(),
      })}
    />
  );
}
